import {
  FieldData,
  MultiSelect,
  PartnerOnboardingEntityType,
  PartnerOnboardingStatus,
  Select,
  TextField,
  useForm,
} from 'vopay';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import usePartnerAccountStatusLabel from '@/hooks/usePartnerAccountStatusLabel';
import { OnboardingFilterValues } from '../types';

type AccountTypeOption = { value: PartnerOnboardingEntityType | 'All'; label: string };

export type UseOnboardingFiltersFormConfigParams = {
  /** The applied filters. Re-seeded on change, so a summary pill click or a `?stage=` link shows in the form. */
  initialValues: OnboardingFilterValues;
  /** Status dropdown options in display order. */
  statuses: PartnerOnboardingStatus[];
  onSubmit: (values: OnboardingFilterValues) => void;
};

export default function useOnboardingFiltersFormConfig({
  initialValues,
  statuses,
  onSubmit,
}: UseOnboardingFiltersFormConfigParams) {
  const config = useConfig(statuses);

  const { renderFormElements, ctx } = useForm<OnboardingFilterValues>({
    initialValues,
    config,
    onSubmit,
    // The page owns the applied filters and changes them without remounting this form: a summary pill
    // click sets the status, and `?stage=&accountType=` seeds them. Formik deep-compares, so a new
    // object with the same values doesn't wipe what the user is typing.
    enableReinitialize: true,
  });

  return {
    ctx,
    renderFormElements: () => renderFormElements({ config }),
  };
}

function useConfig(statuses: PartnerOnboardingStatus[]): FieldData<OnboardingFilterValues>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'OnboardingTracker.OnboardingFilters' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();

  const accountTypeOptions = useMemo<AccountTypeOption[]>(
    () => [
      { value: 'All', label: tGlobal('all') },
      { value: 'Partner', label: t('AccountTypeOptions.partner', { defaultValue: 'Partner' }) },
      { value: 'Account', label: t('AccountTypeOptions.accounts', { defaultValue: 'Accounts' }) },
      { value: 'SubAccount', label: t('AccountTypeOptions.subAccounts', { defaultValue: 'Sub-Accounts' }) },
    ],
    [t, tGlobal],
  );

  // '' is "All": Select2 shows the placeholder for it, which matches the Figma filter bar at rest.
  const statusOptions = useMemo(
    () => [
      { value: '', label: tGlobal('all') },
      ...statuses.map((status) => ({ value: status, label: resolveStatusLabel(status) })),
    ],
    [statuses, resolveStatusLabel, tGlobal],
  );

  return [
    {
      fieldName: 'entityTypes',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ ctx, getFormikData }) => (
        <MultiSelect
          {...getFormikData<string[], OnboardingFilterValues['entityTypes'], { options: AccountTypeOption[] }>({
            // Judgement call: Confluence §6.3 lists All as a value of the multi-select, Figma only shows
            // the field at rest. Stored [] means all types (the API then sends no EntityType), shown
            // as All checked. Picking All clears the specific types; picking a type unchecks All.
            formatValue: (entityTypes) => (entityTypes.length > 0 ? entityTypes : ['All']),
            onChange: (event, args) => {
              const picked = args?.options.map((option) => option.value) ?? [];
              const isAllPicked = picked.includes('All') && ctx.values.entityTypes.length > 0;

              ctx.setFieldValue(
                event.target.name,
                isAllPicked ? [] : picked.filter((value): value is PartnerOnboardingEntityType => value !== 'All'),
              );
            },
          })}
          sort={false}
          placeholder={t('accountTypePlaceholder', { defaultValue: 'Account Type' })}
          options={accountTypeOptions}
          // All alone shows the placeholder, as the Status select does for its All, matching Figma.
          renderSelectedValue={(selected, { Defaults, Placeholder }) =>
            selected.every((option) => option.value === 'All') ? <Placeholder /> : <Defaults />
          }
        />
      ),
    },
    {
      fieldName: 'status',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          {...getFormikData<OnboardingFilterValues['status']>({})}
          sort={false}
          placeholder={t('statusPlaceholder', { defaultValue: 'Onboarding Status' })}
          options={statusOptions}
        />
      ),
    },
    {
      fieldName: 'keyword',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <TextField
          variant="outlineShadow"
          {...getFormikData<OnboardingFilterValues['keyword']>({})}
          placeholder={t('keywordPlaceholder', { defaultValue: 'Keyword' })}
        />
      ),
    },
  ];
}
